/**
 * Ride Schedule Cache Service
 * Caches ride schedule data to avoid repeated API calls
 * 
 * Cache Strategy:
 * - In-memory cache for fast access during session
 * - sessionStorage for persistence across page refreshes
 * - TTL-based expiration for ride schedules
 */

class RideScheduleCache {
  constructor() {
    // In-memory cache: Map<'rideSchedules', { data: Array, timestamp: number }>
    this.memoryCache = new Map();
    
    // Cache configuration
    this.config = {
      // Ride schedules expire after 5 minutes (they can change frequently)
      RIDE_SCHEDULES_TTL: 5 * 60 * 1000, // 5 minutes in milliseconds
      // Storage key for sessionStorage
      STORAGE_KEY: 'rideScheduleCache',
    };
    
    // Load cache from sessionStorage on initialization
    this.loadFromStorage();
  }

  /**
   * Get cache key for ride schedules
   */
  getCacheKey() {
    return 'rideSchedules';
  }

  /**
   * Load cache from sessionStorage
   */
  loadFromStorage() {
    try {
      const stored = sessionStorage.getItem(this.config.STORAGE_KEY);
      if (stored) {
        const cacheData = JSON.parse(stored);
        const now = Date.now();
        
        // Load valid entries only
        for (const [key, value] of Object.entries(cacheData)) {
          // Check if entry is still valid
          const age = now - value.timestamp;
          
          if (age < this.config.RIDE_SCHEDULES_TTL) {
            this.memoryCache.set(key, value);
          }
        }
        
        console.log(`📦 Loaded ${this.memoryCache.size} cached ride schedule entries from storage`);
      }
    } catch (error) {
      console.warn('⚠️ Failed to load ride schedule cache from storage:', error);
    }
  }

  /**
   * Save cache to sessionStorage
   */
  saveToStorage() {
    try {
      const cacheData = {};
      this.memoryCache.forEach((value, key) => {
        cacheData[key] = value;
      });
      
      sessionStorage.setItem(this.config.STORAGE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('⚠️ Failed to save ride schedule cache to storage:', error);
      // If storage is full, clear cache
      this.clearAll();
    }
  }

  /**
   * Check if a cache entry is valid
   */
  isValid(entry) {
    if (!entry) return false;
    
    const now = Date.now();
    const age = now - entry.timestamp;
    
    return age < this.config.RIDE_SCHEDULES_TTL;
  }

  /**
   * Get cached ride schedules
   * @returns {Array|null} - Cached ride schedules or null if not found/invalid
   */
  get() {
    const key = this.getCacheKey();
    const entry = this.memoryCache.get(key);
    
    if (!entry || !entry.data) return null;
    
    // Check if entry is still valid
    if (!this.isValid(entry)) {
      // Cache expired, remove it
      this.memoryCache.delete(key);
      this.saveToStorage();
      return null;
    }
    
    console.log(`✅ Cache hit for ride schedules`);
    return entry.data;
  }

  /**
   * Set cached ride schedules
   * @param {Array} schedules - Ride schedules array
   */
  set(schedules) {
    if (!schedules || !Array.isArray(schedules)) return;
    
    const key = this.getCacheKey();
    
    const entry = {
      data: schedules,
      timestamp: Date.now(),
    };
    
    this.memoryCache.set(key, entry);
    this.saveToStorage();
    
    console.log(`💾 Cached ${schedules.length} ride schedules`);
  }

  /**
   * Clear cached ride schedules
   */
  clear() {
    const key = this.getCacheKey();
    this.memoryCache.delete(key);
    this.saveToStorage();
    console.log(`🗑️ Cleared ride schedule cache`);
  }

  /**
   * Clear all cached data
   */
  clearAll() {
    this.memoryCache.clear();
    try {
      sessionStorage.removeItem(this.config.STORAGE_KEY);
    } catch (error) {
      console.warn('⚠️ Failed to clear cache from storage:', error);
    }
    console.log('🗑️ Cleared all ride schedule cache');
  }

  /**
   * Get cache statistics
   */
  getStats() {
    const key = this.getCacheKey();
    const entry = this.memoryCache.get(key);
    
    return {
      hasCachedData: !!entry && this.isValid(entry),
      cacheSize: entry?.data?.length || 0,
      cacheAge: entry ? Date.now() - entry.timestamp : null,
      ttl: this.config.RIDE_SCHEDULES_TTL,
    };
  }
}

// Export singleton instance
export default new RideScheduleCache();

